/**
 * freshSTART motion engine (PRP-15, D3). One IntersectionObserver reveals
 * `[data-reveal]` elements and `[data-reveal-stagger]` groups once; one
 * MutationObserver picks up nodes added by route changes and WordPress data; one
 * delegated pointermove listener drives the desktop spotlight, magnetic buttons and
 * hero pointer parallax. Pages only add data attributes; the visuals live in
 * src/styles/motion.css.
 *
 * `motion-ok` on <html> gates every hidden starting state. It is added only when
 * reduced motion is not requested and IntersectionObserver exists, before React
 * renders (initMotion runs in main.tsx), so nothing flashes.
 */

const REDUCE = '(prefers-reduced-motion: reduce)';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const REVEAL_SELECTOR = '[data-reveal],[data-reveal-stagger]';
const MAX_STAGGER = 6;
const MAGNET_PULL = 0.25;
const MAGNET_MAX = 6;

let teardown: (() => void) | null = null;

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(REDUCE).matches;
}

function markRevealed(el: Element): void {
  el.setAttribute('data-in', '');
  el.classList.add('is-in');
  if (el.hasAttribute('data-reveal-stagger')) {
    for (const child of Array.from(el.children)) child.classList.add('is-in');
  }
}

function indexStagger(el: Element): void {
  Array.from(el.children).forEach((child, i) => {
    (child as HTMLElement).style.setProperty('--i', String(Math.min(i, MAX_STAGGER)));
  });
}

/** Start the engine. Idempotent: a second call is a no-op until the returned
 *  cleanup runs (tests use the cleanup). */
export function initMotion(): () => void {
  if (teardown) return () => {};
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const html = document.documentElement;
  const reduceQuery = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCE) : null;
  let stopEngine: (() => void) | null = null;

  function startEngine(): void {
    if (stopEngine || prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    html.classList.add('motion-ok');

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // Also reveal anything already scrolled past: content that loads late (WordPress
        // data) can be bound while it sits above the viewport and would never enter it.
        if (!entry.isIntersecting && entry.boundingClientRect.bottom > 0) continue;
        markRevealed(entry.target);
        io.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    const bind = (scope: ParentNode) => {
      scope.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
        if (el.hasAttribute('data-motion-bound')) return;
        el.setAttribute('data-motion-bound', '');
        if (el.hasAttribute('data-reveal-stagger')) indexStagger(el);
        if (!el.hasAttribute('data-in')) io.observe(el);
      });
    };
    bind(document);

    // New nodes: batch per frame. Children added to an already-bound stagger group
    // (WordPress data replacing skeletons) are re-indexed; they reveal through the
    // parent's [data-in] in CSS.
    let queued = false;
    const staggerParents = new Set<Element>();
    const flush = () => {
      queued = false;
      bind(document);
      staggerParents.forEach((p) => {
        indexStagger(p);
        if (p.hasAttribute('data-in')) markRevealed(p);
      });
      staggerParents.clear();
    };
    const mo = new MutationObserver((records) => {
      for (const r of records) {
        const parent = r.target as Element;
        if (parent.nodeType === 1 && parent.hasAttribute('data-reveal-stagger')) staggerParents.add(parent);
      }
      if (!queued) {
        queued = true;
        requestAnimationFrame(flush);
      }
    });
    const root = document.getElementById('root') ?? document.body;
    mo.observe(root, { childList: true, subtree: true });

    // Safety net: anything above the bottom of the viewport that is still hidden
    // 3 s after load (an observer that never fired) is revealed.
    let safetyTimer = 0;
    const safety = () => {
      safetyTimer = window.setTimeout(() => {
        document.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
          if (!el.hasAttribute('data-in') && el.getBoundingClientRect().top < window.innerHeight) {
            markRevealed(el);
            io.unobserve(el);
          }
        });
      }, 3000);
    };
    if (document.readyState === 'complete') safety();
    else window.addEventListener('load', safety, { once: true });

    const stopPointer = startPointerEffects();

    stopEngine = () => {
      io.disconnect();
      mo.disconnect();
      window.clearTimeout(safetyTimer);
      window.removeEventListener('load', safety);
      stopPointer();
      stopEngine = null;
    };
  }

  function revealEverything(): void {
    html.classList.remove('motion-ok');
    document.querySelectorAll(REVEAL_SELECTOR).forEach(markRevealed);
    stopEngine?.();
  }

  const onReduceChange = (e: MediaQueryListEvent) => {
    if (e.matches) revealEverything();
    else startEngine();
  };
  reduceQuery?.addEventListener?.('change', onReduceChange);

  startEngine();

  teardown = () => {
    reduceQuery?.removeEventListener?.('change', onReduceChange);
    stopEngine?.();
    html.classList.remove('motion-ok');
    teardown = null;
  };
  return teardown;
}

/** Spotlight, magnetic and pointer-parallax effects. Desktop fine pointers only;
 *  one passive listener, at most one frame of work per animation frame, CSS
 *  variables and the `translate` property only (no React state). */
function startPointerEffects(): () => void {
  const fine = typeof window.matchMedia === 'function' ? window.matchMedia(FINE_POINTER) : null;
  let lastEvent: PointerEvent | null = null;
  let frame = 0;
  let litHost: HTMLElement | null = null;
  let magnet: HTMLElement | null = null;
  let parallaxHost: HTMLElement | null = null;
  const hasParallax = new WeakMap<Element, boolean>();

  const releaseMagnet = () => {
    if (!magnet) return;
    magnet.style.translate = '';
    magnet.classList.add('magnet-release');
    magnet = null;
  };
  const setLit = (host: HTMLElement | null) => {
    if (litHost === host) return;
    litHost?.classList.remove('is-lit');
    litHost = host;
    litHost?.classList.add('is-lit');
  };
  const setParallaxHost = (host: HTMLElement | null) => {
    if (parallaxHost === host) return;
    if (parallaxHost) {
      parallaxHost.style.removeProperty('--px');
      parallaxHost.style.removeProperty('--py');
    }
    parallaxHost = host;
  };
  const resetAll = () => {
    setLit(null);
    releaseMagnet();
    setParallaxHost(null);
  };

  const process = () => {
    frame = 0;
    const e = lastEvent;
    if (!e) return;
    const target = e.target instanceof Element ? e.target : null;
    if (!target || target.closest('[data-motion-quiet]')) {
      resetAll();
      return;
    }

    const spot = target.closest<HTMLElement>('[data-spotlight]');
    setLit(spot);
    if (spot) {
      const r = spot.getBoundingClientRect();
      spot.style.setProperty('--mx', `${e.clientX - r.left}px`);
      spot.style.setProperty('--my', `${e.clientY - r.top}px`);
    }

    const m = target.closest<HTMLElement>('[data-magnetic]');
    if (m !== magnet) releaseMagnet();
    if (m) {
      magnet = m;
      m.classList.remove('magnet-release');
      const r = m.getBoundingClientRect();
      const clamp = (v: number) => Math.max(-MAGNET_MAX, Math.min(MAGNET_MAX, v));
      const dx = clamp((e.clientX - (r.left + r.width / 2)) * MAGNET_PULL);
      const dy = clamp((e.clientY - (r.top + r.height / 2)) * MAGNET_PULL);
      m.style.translate = `${dx.toFixed(2)}px ${dy.toFixed(2)}px`;
    }

    const section = target.closest<HTMLElement>('section');
    let host: HTMLElement | null = null;
    if (section) {
      if (!hasParallax.has(section)) hasParallax.set(section, !!section.querySelector('[data-pointer-parallax]'));
      if (hasParallax.get(section)) host = section;
    }
    setParallaxHost(host);
    if (host) {
      const r = host.getBoundingClientRect();
      const px = ((e.clientX - r.left) / r.width) * 2 - 1;
      const py = ((e.clientY - r.top) / r.height) * 2 - 1;
      host.style.setProperty('--px', Math.max(-1, Math.min(1, px)).toFixed(3));
      host.style.setProperty('--py', Math.max(-1, Math.min(1, py)).toFixed(3));
    }
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' || !fine?.matches) return;
    lastEvent = e;
    if (!frame) frame = requestAnimationFrame(process);
  };
  const onLeave = () => {
    lastEvent = null;
    resetAll();
  };

  document.addEventListener('pointermove', onMove, { passive: true });
  document.documentElement.addEventListener('pointerleave', onLeave, { passive: true });
  return () => {
    document.removeEventListener('pointermove', onMove);
    document.documentElement.removeEventListener('pointerleave', onLeave);
    if (frame) cancelAnimationFrame(frame);
    resetAll();
  };
}
